import { useMemo, useState, useEffect, useId } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge, badgeVariants } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Package,
  Filter,
  Search,
  Eye,
  Edit,
  MessageSquare,
  Loader2,
  Calendar as CalendarIcon,
  ArrowUpDown,
  Check,
  ChevronsUpDown,
  History,
  FileDown,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  CheckCircle2,
  Download,
  RefreshCw,
  Truck,
  BookOpen,
  Layers,
  FileText,
} from "lucide-react";
import { type VariantProps } from "class-variance-authority";

import { formatCurrency, formatDate, formatDateTime } from "@/lib/formatters";
import { getInstallmentsByOrder, StatusType } from "@/lib/mock-data/mockFinancialData";
import { cn } from "@/lib/utils";
import { TooltipWrapper } from "@/components/shared/TooltipWrapper";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { toast } from "@/components/ui/sonner";
import { ErrorState } from "@/components/shared/ErrorState";
import { ShortcutConfig, useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { Order } from "@/lib/api";
import { useOrderManagement, OrderExtended } from "@/hooks/useOrderManagement";
import { useOrderMetrics } from "@/hooks/useOrderMetrics";
import { Skeleton } from "@/components/ui/skeleton";
import { useRegeneratePDF } from "@/hooks/useRegeneratePDF";
import { isOrderCancelled, resolveDisplayOrderStatus } from "@/lib/orderStatus";

import { OrderManagementSkeleton } from "./component/OrderManagementSkeleton";
import { EmptyState } from "../CustomerPortal/Financial/component/EmptyState";
import StateBadge from "@/components/ui/stateBadge";
import { StatusOrdersCompleted } from "@/components/shared/StatusOrdersCompleted";

export const allStatusTypes: StatusType[] = [
  "Criado",
  "Em Rota",
  "Pendente",
  "Processado",
  "Entregue",
  "Cancelado"
];

const OrderManagement = () => {
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const {
    searchRef,
    tableData,
    totalRows,
    totalPages,
    sorting,
    setSorting,
    rowSelection,
    setRowSelection,
    pagination,
    setPagination,
    filters,
    setFilters,
    handleClearFilters,
    isLoading,
    isFetching,
    allOrders,
    error,
    refetch,
  } = useOrderManagement();

  const clientLabelId = useId();
  const quickSearchId = useId();
  const rowsPerPageLabelId = useId();

  const { metrics, clientsForFilter, isLoadingMetrics } = useOrderMetrics();

  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [bulkNewStatus, setBulkNewStatus] = useState<StatusType>("Processado");
  const [clientSearchOpen, setClientSearchOpen] = useState(false);

  useKeyboardShortcuts([
    { key: "f", ctrlKey: true, action: () => searchRef.current?.focus() },
    { key: "Escape", action: () => setBulkActionDialogOpen(false) }
  ] as ShortcutConfig[]);

  const ordersExtended = useMemo((): OrderExtended[] => {
    return (
      tableData?.map((order) => {
        const installments = getInstallmentsByOrder(order.id);
        const paidInstallments = installments.filter((i) => i.status === "paid");
        const overdueInstallments = installments.filter((i) => i.status === "overdue");
        const pendingInstallments = installments.filter((i) => i.status === "pending");

        let financialStatus: OrderExtended["financialStatus"] = "pending";
        if (overdueInstallments.length > 0) financialStatus = "overdue";
        else if (paidInstallments.length > 0 && pendingInstallments.length > 0) financialStatus = "partial";
        else if (paidInstallments.length === installments.length && installments.length > 0) financialStatus = "paid";

        return {
          ...order,
          financialStatus,
          overdueAmount: overdueInstallments.reduce((sum, i) => sum + i.amount, 0),
          lastUpdate: order.updatedAt || new Date().toISOString(),
        };
      }) || []
    );
  }, [tableData]);

  const selectedOrdersCount = Object.keys(rowSelection).filter(key => rowSelection[key]).length;

  const hasActiveFilters = useMemo(() => {
    return (
      filters.status !== "all" ||
      filters.client !== "all" ||
      filters.searchQuery !== "" ||
      filters.minValue !== "" ||
      filters.maxValue !== "" ||
      filters.deliveryType !== "all" ||
      filters.type !== "all" ||
      !!filters.dateRange?.from
    );
  }, [filters]);

  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const { regeneratePDF } = useRegeneratePDF();

  const handleDownloadPDF = async (orderId: string, orderNumber: string) => {
    setRegeneratingId(orderId);
    try {
      const orderItem = ordersExtended?.find((o) => o.id === orderId);
      if (orderItem) await regeneratePDF(orderItem);
    } finally {
      setRegeneratingId(null);
    }
  };

  const columns: ColumnDef<OrderExtended>[] = [
    {
      accessorKey: "orderNumber",
      header: ({ column }) => {
        const isSorted = column.getIsSorted();
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(isSorted === "asc")}
            aria-label={`ID Pedido: clique para ordenar ${isSorted === "asc" ? "descendente" : "ascendente"}`}
          >
            ID Pedido <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
          </Button>
        );
      },
      cell: ({ row }) => <div className="font-mono font-medium">{row.getValue("orderNumber")}</div>
    },
    {
      accessorKey: "clientName",
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Cliente <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      ),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.clientName}</div>
          <div className="text-xs text-muted-foreground">{row.original.clientCode}</div>
        </div>
      )
    },
    {
      accessorKey: "orderDate",
      header: "Data do Pedido",
      cell: ({ row }) => formatDate(new Date(row.original.createdAt)),
    },
    {
      accessorKey: "deliveryDate",
      header: "Previsão de Entrega",
      cell: ({ row }) => row.original?.previsionDelivery != null ? formatDate(new Date(row.original?.previsionDelivery)) : '-',
    },
    {
      accessorKey: "cycleCode",
      header: "Tipo",
      cell: ({ row }) => <StatusOrdersCompleted shipmentBlock={row.original.shipmentBlock} orderNumber={row.original.orderNumber} cycleCode={row.original.cycleCode} />
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        if (isOrderCancelled(row.original)) return <StateBadge status="Cancelado" />
        return <StateBadge status={resolveDisplayOrderStatus(row.original.portalB2BOrderStatus)} />
      }
    },
    {
      accessorKey: "totalAmount",
      header: ({ column }) => <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="text-left w-full">Valor <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" /></Button>,
      cell: ({ row }) => {
        const total = Number(row.getValue("totalAmount")) + row.original.shippingAmount;
        return <div className="text-left font-medium">{formatCurrency(total)}</div>
      }
    },
    {
      id: "actions",
      header: () => <div className="text-center">Ações</div>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <TooltipWrapper content="Ver detalhes">
            <Button variant="ghost" size="sm" asChild aria-label={`Ver detalhes do pedido ${row.original.orderNumber}`}>
              <Link to={`/backoffice/pedidos/${row.original.id}`}>
                <Eye className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Ver detalhes</span>
              </Link>
            </Button>
          </TooltipWrapper>
          <TooltipWrapper content="Baixar PDF">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDownloadPDF(row.original.id, row.original.orderNumber)}
              disabled={regeneratingId === row.original.id}
              aria-label={`Baixar PDF do pedido ${row.original.orderNumber}`}
            >
              {regeneratingId === row.original.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" aria-hidden="true" />}
              <span className="sr-only">Baixar PDF</span>
            </Button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: ordersExtended,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    state: { sorting, rowSelection, pagination },
  });

  if (isLoading) return <OrderManagementSkeleton />;
  if (error) return <ErrorState message="Não foi possível carregar os pedidos." onRetry={refetch} details={error.message} />;

  return (
    <>
      <ConfirmDialog />
      <div className="min-h-screen bg-background">
        <header className="border-b bg-slate-900 text-slate-100" role="banner">
          <div className="container mx-auto px-6 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight" id="main-heading">Gestão de Pedidos</h1>
                <p className="text-sm text-slate-400">
                  <span aria-live="polite">{allOrders.length} pedido{allOrders.length !== 1 ? 's' : ''} no total</span>
                </p>
              </div>
              {selectedOrdersCount > 0 && (
                <div className="flex items-center gap-3 bg-blue-900/30 px-4 py-2 rounded-lg border border-blue-700" role="toolbar" aria-label="Ações em lote">
                  <span className="text-sm font-medium" aria-live="polite">{selectedOrdersCount} selecionados</span>
                  <Separator orientation="vertical" className="h-6 bg-slate-700" aria-hidden="true" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBulkActionDialogOpen(true)}
                    aria-label={`Alterar status de ${selectedOrdersCount} pedidos selecionados`}
                  >
                    <Edit className="h-4 w-4 mr-2" aria-hidden="true" /> Status
                  </Button>
                </div>
              )}
            </div>

            <section aria-labelledby="metrics-heading">
              <h2 id="metrics-heading" className="sr-only">Resumo de métricas dos pedidos</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                {[
                  { label: "Criados", value: metrics.created, color: "text-blue-500", icon: FileText },
                  { label: "Pendentes", value: metrics.pending, color: "text-yellow-500", icon: Clock },
                  { label: "Processado", value: metrics.processing, color: "text-blue-500", icon: Package },
                  { label: "Em Rota", value: metrics.enRoute, color: "text-teal-500", icon: Truck },
                  { label: "Entregues", value: metrics.delivered, color: "text-green-500", icon: CheckCircle2 },
                  { label: "Cancelados", value: metrics.canceled, color: "text-red-500", icon: History }
                ].map((m, i) => (
                  <Card key={i} className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-400">{m.label}</p>
                          <p className={cn("text-2xl font-bold", m.color)}>{m.value}</p>
                        </div>
                        <m.icon className={cn("h-8 w-8 opacity-50", m.color)} aria-hidden="true" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </div>
        </header>

        <main className="container mx-auto px-6 py-8 space-y-6" role="main" aria-labelledby="main-heading">
          {/* Status Announcer for Screen Readers - WCAG 4.1.3 */}
          <div className="sr-only" aria-live="polite" role="status">
            {hasActiveFilters
              ? `${totalRows} pedidos encontrados para os filtros aplicados.`
              : `Mostrando todos os ${totalRows} pedidos.`}
          </div>

          <section aria-labelledby="filters-heading">
            <Card className={cn("border-border shadow-lg", hasActiveFilters && "border-primary/20 bg-primary/5")}>
              <CardHeader>
                <CardTitle id="filters-heading" className="text-lg font-semibold flex items-center gap-2">
                  <Filter className="h-5 w-5" aria-hidden="true" /> Filtros Avançados
                  {hasActiveFilters && <Badge variant="secondary" className="ml-2">Ativos</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="grid gap-4 md:grid-cols-7">
                  <div className="space-y-2">
                    <Label htmlFor="date-range-picker">Período</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button id="date-range-picker" variant="outline" className="w-full justify-start text-left font-normal" aria-label="Filtrar por data. Período selecionado: de acordo com a seleção abaixo.">
                          <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                          {filters.dateRange?.from ? format(filters.dateRange.from, "dd/MM/yy") : "Filtrar por data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="range" selected={filters.dateRange} onSelect={(r) => setFilters(f => ({ ...f, dateRange: r }))} locale={ptBR} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status-filter">Status</Label>
                    <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                      <SelectTrigger id="status-filter" aria-label="Selecionar status para filtrar"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os Status</SelectItem>
                        {allStatusTypes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type-filter">Tipo</Label>
                    <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
                      <SelectTrigger id="type-filter" aria-label="Selecionar tipo para filtrar"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os Tipos</SelectItem>
                        <SelectItem value="simulado">Avaliações e Simulados</SelectItem>
                        <SelectItem value="anual">Anual</SelectItem>
                        <SelectItem value="semestral">Semestral</SelectItem>
                        <SelectItem value="segundo_semestre">2º Semestre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label id={clientLabelId}>Cliente</Label>
                    <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" aria-labelledby={clientLabelId} aria-expanded={clientSearchOpen} className="w-full justify-between" aria-haspopup="listbox">
                          {filters.client !== "all" ? clientsForFilter.find(c => c.clientCode === filters.client)?.name : "Selecionar cliente..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0">
                        <Command>
                          <CommandInput placeholder="Buscar cliente..." aria-label="Digitar nome do cliente para buscar" />
                          <CommandList>
                            <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem value="all" onSelect={() => { setFilters(f => ({ ...f, client: "all" })); setClientSearchOpen(false); }}>Todos</CommandItem>
                              {clientsForFilter.map((c) => (
                                <CommandItem key={c.clientCode} onSelect={() => { setFilters(f => ({ ...f, client: c.clientCode })); setClientSearchOpen(false); }}>
                                  {c.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor={quickSearchId}>Busca Rápida</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                      <Input id={quickSearchId} ref={searchRef} placeholder="ID, cliente ou CNPJ..." value={filters.searchQuery} onChange={(e) => setFilters(f => ({ ...f, searchQuery: e.target.value }))} className="pl-10" aria-label="Busca rápida por ID, cliente ou CNPJ" />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    variant={!hasActiveFilters ? 'ghost' : 'outline'}
                    size="sm"
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters}
                    className="text-xs"
                    aria-label="Limpar todos os filtros aplicados"
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>

          <section aria-labelledby="table-heading">
            <h2 id="table-heading" className="sr-only">Listagem de pedidos</h2>
            <Card className="border-border shadow-lg">
              <CardContent className="p-0">
                <div className="relative" aria-live="polite" aria-busy={isFetching}>
                  {isFetching && (
                    <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="sr-only">Carregando pedidos...</span>
                    </div>
                  )}
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => (
                          <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                              <TableHead key={header.id} scope="col">
                                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                              </TableHead>
                            ))}
                          </TableRow>
                        ))}
                      </TableHeader>
                      <TableBody>
                        {table.getRowModel().rows?.length ? table.getRowModel().rows.map(row => (
                          <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                            {row.getVisibleCells().map(cell => <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>)}
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">Nenhum pedido encontrado.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>

              {totalPages > 0 && (
                <nav className="flex items-center justify-between p-4 border-t" aria-label="Navegação de página">
                  <div className="text-sm text-muted-foreground" aria-live="polite">{selectedOrdersCount} de {totalRows} selecionados</div>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                      <Label htmlFor={rowsPerPageLabelId} className="text-sm font-medium">Linhas</Label>
                      <Select value={`${pagination.pageSize}`} onValueChange={v => table.setPageSize(Number(v))}>
                        <SelectTrigger id={rowsPerPageLabelId} className="h-8 w-[70px]" aria-label="Quantidade de linhas por página"><SelectValue /></SelectTrigger>
                        <SelectContent side="top">
                          {[15, 30, 50].map(s => <SelectItem key={s} value={`${s}`}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-sm font-medium" aria-live="polite">Página {pagination.pageIndex + 1} de {totalPages}</div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" className="h-8 w-8 p-0" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} aria-label="Ir para a página anterior">
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button variant="outline" className="h-8 w-8 p-0" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} aria-label="Ir para a próxima página">
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                </nav>
              )}
            </Card>
          </section>
        </main>
      </div>
    </>
  );
};

export default OrderManagement;
